/**
 * ContractDialog — the modal shell for the notice composer and the flag
 * forms: `role="dialog"`, labelled, focus moved in on open and back on close,
 * focus kept inside (Tab wraps; focus that lands outside, or is lost when the
 * focused control unmounts, is brought back in), Escape closes (unless `busy`).
 *
 * Multi-step content passes `stepKey` and `stepAnnouncement`: on every change
 * of `stepKey` focus moves to the step's start and the announcement is read
 * by a polite live region. The step's start is, in order: an element marked
 * `data-step-focus`, the first enabled form field, else the dialog's heading.
 */

import React, { useCallback, useEffect, useId, useRef } from 'react';
import styles from './ContractDialog.module.css';

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
const FIELDS = 'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled])';

export default function ContractDialog({
  title,
  eyebrow,
  onClose,
  busy = false,
  children,
  testId,
  wide = false,
  stepKey,
  stepAnnouncement,
}) {
  const panelRef = useRef(null);
  const bodyRef = useRef(null);
  const titleRef = useRef(null);
  const titleId = `contract-dialog-${useId()}`;
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  const lastStepRef = useRef(stepKey);
  // The live region stays empty on open (the dialog's name is read then) and
  // carries the current step's announcement from the first step change on,
  // updated in the same render as the step.
  const initialStepRef = useRef(stepKey);
  const steppedRef = useRef(false);
  if (stepKey !== initialStepRef.current) steppedRef.current = true;
  const announcement = steppedRef.current && stepAnnouncement ? stepAnnouncement : '';
  onCloseRef.current = onClose;
  busyRef.current = busy;

  /** The first or last element Tab can reach inside the panel, else the panel. */
  const edgeItem = useCallback((last = false) => {
    const panel = panelRef.current;
    if (!panel) return null;
    const items = [...panel.querySelectorAll(FOCUSABLE)];
    if (items.length === 0) return panel;
    return last ? items[items.length - 1] : items[0];
  }, []);

  useEffect(() => {
    const previous = typeof document !== 'undefined' ? document.activeElement : null;
    const panel = panelRef.current;
    const first = bodyRef.current?.querySelector(FOCUSABLE);
    (first || panel)?.focus();

    const onKey = (event) => {
      if (event.key === 'Escape' && !busyRef.current) {
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const active = document.activeElement;
      // Focus was lost (its control unmounted) or left the panel: bring it back.
      if (!active || !panel.contains(active)) {
        event.preventDefault();
        edgeItem(event.shiftKey)?.focus();
        return;
      }
      const items = [...panel.querySelectorAll(FOCUSABLE)];
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (event.shiftKey && (active === firstItem || active === panel)) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && active === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };
    // Focus moved outside by any means (a click on the page, script): return it.
    const onFocusIn = (event) => {
      if (!panel || panel.contains(event.target)) return;
      edgeItem(false)?.focus();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', onFocusIn);
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  }, [edgeItem]);

  // A new step: focus its start and announce it.
  useEffect(() => {
    if (stepKey === undefined || lastStepRef.current === stepKey) return;
    lastStepRef.current = stepKey;
    const body = bodyRef.current;
    const target = body?.querySelector('[data-step-focus]')
      || body?.querySelector(FIELDS)
      || titleRef.current;
    target?.focus();
  }, [stepKey]);

  return (
    <div className={styles.backdrop} data-testid={testId ? `${testId}-backdrop` : undefined}>
      <div
        ref={panelRef}
        className={`${styles.panel} ${wide ? styles.wide : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid={testId}
      >
        <header className={styles.header}>
          <div>
            {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
            <h2 id={titleId} ref={titleRef} className={styles.title} tabIndex={-1}>{title}</h2>
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>
        <p
          className={styles.srOnly}
          aria-live="polite"
          aria-atomic="true"
          data-testid={testId ? `${testId}-step-announcement` : undefined}
        >
          {announcement}
        </p>
        <div ref={bodyRef} className={styles.body}>{children}</div>
      </div>
    </div>
  );
}
